import { CreditCard, FileDownload, Payments, ReceiptLong, Storefront, Style, TableChart } from "@mui/icons-material";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Moment } from "moment";
import { useMemo, useState } from "react";
import { ApiFactory } from "../../../core/config/ApiFactory";
import { TipoCartao } from "../../../core/enum/TipoCartao";
import { useDataHora } from "../../../core/hooks/useDataHora";
import { useFetch } from "../../../core/hooks/useFetch";
import { useMoeda } from "../../../core/hooks/useMoeda";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { useFetchBandeirasCartao } from "../../../pedidos/hooks/useFetchBandeirasCartao";
import { useFetchMaquininhas } from "../../../pedidos/hooks/useFetchMaquininhas";
import { useConciliacaoPagamentos } from "../../hooks/useConciliacaoPagamentos";
import {
    IFiltrosConciliacaoPagamentos,
    IListagemConciliacaoPagamentos,
    ITotalizadorConciliacao
} from "../../interfaces/IConciliacaoPagamentos";
import { CartaoIndicador } from "../Caixa/components/CartaoIndicador";
import { TabelaResumo } from "../Caixa/components/TabelaResumo";

const corStatus = (status: string): "success" | "warning" | "error" | "default" => {
    if (status === "Pago") return "success";
    if (status === "Pendente") return "warning";
    if (status === "Cancelado") return "error";
    return "default";
};

/** O TabelaResumo espera `descricao`; os totalizadores vêm com `chave`. */
const paraResumo = (totalizadores?: ITotalizadorConciliacao[]) =>
    (totalizadores ?? []).map(total => ({
        descricao: total.chave,
        quantidade: total.quantidade,
        valorTotal: total.valorTotal
    }));

/**
 * Relatório de vendas para conciliação de pagamentos.
 *
 * Lista cada pagamento numa linha — um pedido pago em três formas aparece três vezes — que é
 * como a operadora reporta e como a conferência precisa ser feita.
 */
export const RelatorioConciliacaoPagamentos = () => {
    const { real } = useMoeda();
    const { dataBr, horaBr } = useDataHora();
    const snackbar = useSnackbar();
    const { buscar, exportar, loading, loadingExportacao } = useConciliacaoPagamentos();

    const [dataInicio, setDataInicio] = useState<Moment | null>(null);
    const [dataFim, setDataFim] = useState<Moment | null>(null);
    const [metodoPagamento, setMetodoPagamento] = useState('');
    const [maquininhaUuid, setMaquininhaUuid] = useState('');
    const [bandeiraUuid, setBandeiraUuid] = useState('');
    const [tipoCartao, setTipoCartao] = useState('');

    const [dados, setDados] = useState<IListagemConciliacaoPagamentos | null>(null);
    // Guarda os filtros da última consulta: paginar e exportar devem usar o que foi aplicado,
    // não o que está digitado nos campos no momento.
    const [filtrosAplicados, setFiltrosAplicados] = useState<IFiltrosConciliacaoPagamentos | null>(null);

    const { maquininhas } = useFetchMaquininhas();
    const { bandeiras } = useFetchBandeirasCartao();

    const apiPedidos = useMemo(() => ApiFactory.getApi("MS_PEDIDOS"), []);
    const { data: configuracao } = useFetch<{ metodosPagamento?: string[] }>(apiPedidos, '/configuracoes');

    const montarFiltros = (): IFiltrosConciliacaoPagamentos => ({
        dataInicio: dataInicio?.format('YYYY-MM-DD'),
        dataFim: dataFim?.format('YYYY-MM-DD'),
        metodoPagamento: metodoPagamento || undefined,
        maquininhaUuid: maquininhaUuid || undefined,
        bandeiraUuid: bandeiraUuid || undefined,
        tipoCartao: tipoCartao || undefined
    });

    const handleFiltrar = async () => {
        if (!dataInicio || !dataFim) {
            snackbar("Informe a data de início e a data final", { severity: "warning" });
            return;
        }

        const filtros = montarFiltros();
        const resultado = await buscar(filtros, 1, true);

        if (resultado) {
            setDados(resultado);
            setFiltrosAplicados(filtros);
        }
    };

    const handlePaginaChange = async (_evento: unknown, pagina: number) => {
        if (!filtrosAplicados) return;

        // Os totais são do período inteiro e não mudam entre páginas.
        const resultado = await buscar(filtrosAplicados, pagina, false);

        if (resultado) {
            setDados(anterior => ({ ...resultado, totais: anterior?.totais }));
        }
    };

    const handleLimpar = () => {
        setDataInicio(null);
        setDataFim(null);
        setMetodoPagamento('');
        setMaquininhaUuid('');
        setBandeiraUuid('');
        setTipoCartao('');
        setDados(null);
        setFiltrosAplicados(null);
    };

    const handleExportar = (formato: 'xlsx' | 'csv') => {
        if (!filtrosAplicados) {
            snackbar("Gere o relatório antes de exportar", { severity: "warning" });
            return;
        }

        exportar(filtrosAplicados, formato);
    };

    const totais = dados?.totais;

    return (
        <div style={{ maxWidth: '95%', margin: '0 auto', paddingTop: '1rem' }}>
            <Paper sx={{ padding: '1rem', marginBottom: '1rem' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={2}>
                        <DatePicker
                            label="Data início"
                            value={dataInicio}
                            onChange={setDataInicio}
                            format="DD/MM/YYYY"
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <DatePicker
                            label="Data fim"
                            value={dataFim}
                            onChange={setDataFim}
                            format="DD/MM/YYYY"
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Forma de pagamento</InputLabel>
                            <Select
                                label="Forma de pagamento"
                                value={metodoPagamento}
                                onChange={(e) => setMetodoPagamento(e.target.value)}
                            >
                                <MenuItem value=""><em>Todas</em></MenuItem>
                                {configuracao?.metodosPagamento?.map(metodo => (
                                    <MenuItem key={metodo} value={metodo}>{metodo}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Maquininha</InputLabel>
                            <Select
                                label="Maquininha"
                                value={maquininhaUuid}
                                onChange={(e) => setMaquininhaUuid(e.target.value)}
                            >
                                <MenuItem value=""><em>Todas</em></MenuItem>
                                {maquininhas.map(maquininha => (
                                    <MenuItem key={maquininha.uuid} value={maquininha.uuid}>{maquininha.nome}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Bandeira</InputLabel>
                            <Select
                                label="Bandeira"
                                value={bandeiraUuid}
                                onChange={(e) => setBandeiraUuid(e.target.value)}
                            >
                                <MenuItem value=""><em>Todas</em></MenuItem>
                                {bandeiras.map(bandeira => (
                                    <MenuItem key={bandeira.uuid} value={bandeira.uuid}>{bandeira.nome}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Tipo</InputLabel>
                            <Select
                                label="Tipo"
                                value={tipoCartao}
                                onChange={(e) => setTipoCartao(e.target.value)}
                            >
                                <MenuItem value=""><em>Todos</em></MenuItem>
                                {Object.values(TipoCartao).map(tipo => (
                                    <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                    <Button variant="contained" onClick={handleFiltrar} disabled={loading}>
                        {loading ? <CircularProgress size={20} /> : "Gerar relatório"}
                    </Button>
                    <Button variant="text" onClick={handleLimpar} disabled={loading}>
                        Limpar
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        variant="outlined"
                        startIcon={loadingExportacao ? <CircularProgress size={16} /> : <FileDownload />}
                        onClick={() => handleExportar('xlsx')}
                        disabled={loadingExportacao || !dados}
                    >
                        Excel
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={loadingExportacao ? <CircularProgress size={16} /> : <FileDownload />}
                        onClick={() => handleExportar('csv')}
                        disabled={loadingExportacao || !dados}
                    >
                        CSV
                    </Button>
                </Box>
            </Paper>

            {totais && (
                <>
                    <Grid container spacing={2} sx={{ marginBottom: '1rem' }}>
                        <Grid item xs={12} sm={6}>
                            <CartaoIndicador
                                rotulo="Total no período"
                                valor={real(totais.valorTotal)}
                                icone={<Payments />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <CartaoIndicador
                                rotulo="Pagamentos"
                                valor={totais.quantidade}
                                icone={<ReceiptLong />}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ marginBottom: '1rem' }}>
                        <Grid item xs={12} md={4}>
                            <TabelaResumo
                                titulo="Por data"
                                dados={paraResumo(totais.porData)}
                                mensagemVazia="Sem pagamentos no período"
                                icone={<TableChart />}
                                altura={320}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TabelaResumo
                                titulo="Por forma de pagamento"
                                dados={paraResumo(totais.porMetodoPagamento)}
                                mensagemVazia="Sem pagamentos no período"
                                icone={<Payments />}
                                altura={320}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TabelaResumo
                                titulo="Por maquininha/instituição"
                                dados={paraResumo(totais.porMaquininha)}
                                mensagemVazia="Sem pagamentos no período"
                                icone={<Storefront />}
                                altura={320}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TabelaResumo
                                titulo="Por tipo"
                                dados={paraResumo(totais.porTipoCartao)}
                                mensagemVazia="Sem pagamentos no período"
                                icone={<CreditCard />}
                                altura={280}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TabelaResumo
                                titulo="Por bandeira"
                                dados={paraResumo(totais.porBandeira)}
                                mensagemVazia="Sem pagamentos no período"
                                icone={<Style />}
                                altura={280}
                            />
                        </Grid>
                    </Grid>
                </>
            )}

            <Paper sx={{ padding: '1rem' }}>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Data</TableCell>
                                <TableCell>Hora</TableCell>
                                <TableCell>Nº Pedido</TableCell>
                                <TableCell align="right">Valor</TableCell>
                                <TableCell>Forma de pagamento</TableCell>
                                <TableCell>Maquininha/Instituição</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell>Bandeira</TableCell>
                                <TableCell>Nº autorização</TableCell>
                                <TableCell align="center">Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!dados && (
                                <TableRow>
                                    <TableCell colSpan={10} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                            Selecione um período e clique em "Gerar relatório".
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {dados && !dados.itens.length && (
                                <TableRow>
                                    <TableCell colSpan={10} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                            Nenhum pagamento encontrado no período.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {dados?.itens.map((pagamento, indice) => (
                                    <TableRow key={`${pagamento.numeroPedido}-${indice}`}>
                                        <TableCell>{pagamento.dataHora ? dataBr(pagamento.dataHora) : '-'}</TableCell>
                                        <TableCell>{pagamento.dataHora ? horaBr(pagamento.dataHora) : '-'}</TableCell>
                                        <TableCell>{pagamento.numeroPedido ?? '-'}</TableCell>
                                        <TableCell align="right">{real(pagamento.valor)}</TableCell>
                                        <TableCell>{pagamento.metodoPagamento ?? '-'}</TableCell>
                                        <TableCell>{pagamento.maquininhaNome ?? '-'}</TableCell>
                                        <TableCell>{pagamento.tipoCartao ?? '-'}</TableCell>
                                        <TableCell>{pagamento.bandeiraNome ?? '-'}</TableCell>
                                        <TableCell>{pagamento.numeroAutorizacao ?? '-'}</TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                size="small"
                                                label={pagamento.statusPagamento}
                                                color={corStatus(pagamento.statusPagamento)}
                                            />
                                        </TableCell>
                                    </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {dados && dados.quantidadeTotalPaginas > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Exibindo {dados.quantidadePaginaItens} de {dados.quantidadeTotalItens} pagamentos
                        </Typography>
                        <Pagination
                            count={dados.quantidadeTotalPaginas}
                            page={dados.paginaAtual}
                            onChange={handlePaginaChange}
                            disabled={loading}
                        />
                    </Box>
                )}
            </Paper>
        </div>
    );
};
